import { Control, Path, useFieldArray, useFormContext } from 'react-hook-form';
import {
    IJobQuestionsForm,
    ISaveJobForm,
    JobQuestionObjectFields,
} from '../../../../../common';

interface UseQuestionOptionsProps {
    control: Control<IJobQuestionsForm>;
    fieldNamePrefix: `questions.${number}`;
}

const isPersistedOptionValue = (value: string | undefined) =>
    !!value && !String(value).startsWith('new_');

export const useQuestionOptions = ({
    control,
    fieldNamePrefix,
}: UseQuestionOptionsProps) => {
    const { getValues, setValue } = useFormContext<ISaveJobForm>();
    const optionsFieldArray = useFieldArray({
        control,
        name: `${fieldNamePrefix}.${JobQuestionObjectFields.OPTIONS}` as `questions.${number}.options`,
    });

    const {
        fields: optionFields,
        append: appendOption,
        remove: removeOption,
    } = optionsFieldArray;

    const handleAddOption = () => {
        appendOption({
            value: `new_${new Date().getTime()}`,
            label: '',
            isCorrectAnswer: false,
        });
    };

    const handleRemoveOption = (optionIndex: number) => {
        const optionsPath =
            `${fieldNamePrefix}.${JobQuestionObjectFields.OPTIONS}` as Path<ISaveJobForm>;
        const deletedPath =
            `${fieldNamePrefix}.${JobQuestionObjectFields.DELETED_OPTIONS}` as Path<ISaveJobForm>;
        const options =
            (getValues(optionsPath) as
                | Array<{ value: string; label: string }>
                | undefined) || [];
        const option = options[optionIndex];

        if (option && isPersistedOptionValue(String(option.value))) {
            const deletedOptions =
                (getValues(deletedPath) as Array<{ value: string }> | undefined) ||
                [];
            setValue(
                deletedPath,
                [...deletedOptions, { value: String(option.value) }] as never,
                { shouldDirty: true }
            );
        }

        removeOption(optionIndex);
    };

    return {
        optionFields,
        appendOption,
        removeOption: handleRemoveOption,
        handleAddOption,
    };
};
